import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    X, BarChart3, LineChart as LineChartIcon, Table2,
    Calendar, Loader2, Info, ChevronDown, ChevronUp, Search, User
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { buscarTudoPorChave } from '../../src/lib/paginacaoKeyset';
import { criarChecagemTarefaArquivada } from '../../src/lib/tarefaArquivada';
import { useAuth } from '../../src/contexts/AuthContext';
import {
    ResponsiveContainer, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ResponsibleFilter from '../../src/components/ResponsibleFilter';

interface ModalTasksGraphicProps {
    isOpen: boolean;
    onClose: () => void;
    selectedResponsibles: string[];
}

interface TarefaRow {
    id: number;
    created_at: string;
    vencimento: string | null;
    status: string;
    prioridade: string;
    responsavel_id: string | null;
    data_conclusao?: string | null;
    categoria?: string | null;
}

interface ProfileItem {
    id: string;
    full_name: string;
    parceiro_id?: string;
    role?: string;
    departamento?: string;
}

const ModalTasksGraphic: React.FC<ModalTasksGraphicProps> = ({
    isOpen,
    onClose,
    selectedResponsibles: externalResponsibles,
}) => {
    const { profile, user } = useAuth();

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const [dateStart, setDateStart] = useState(firstDay.toISOString().split('T')[0]);
    const [dateEnd, setDateEnd] = useState(lastDay.toISOString().split('T')[0]);
    const [chartType, setChartType] = useState<'line' | 'table'>('line');
    const [compareMetric, setCompareMetric] = useState<'criadas' | 'concluidas' | 'atrasadas'>('concluidas');
    const [onlyBusinessDays, setOnlyBusinessDays] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<TarefaRow[]>([]);

    const [profiles, setProfiles] = useState<ProfileItem[]>([]);
    const [dashboardResponsibles, setDashboardResponsibles] = useState<string[]>([]);
    const [responsibleMode, setResponsibleMode] = useState<'all' | 'mine' | 'custom'>('all');
    const [pendingResponsibles, setPendingResponsibles] = useState<string[]>([]);
    const [pendingMode, setPendingMode] = useState<'all' | 'mine' | 'custom'>('all');
    const [showResponsibleDropdown, setShowResponsibleDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isVendedor = profile?.role === 'vendedor';
    const isComparative = responsibleMode === 'custom' && dashboardResponsibles.length >= 2;
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];

    useEffect(() => {
        if (!isOpen) return;
        const fetchProfiles = async () => {
            const { data } = await supabase.from('profiles').select('id, full_name, parceiro_id, departamento, role').eq('status', 'active').neq('role', 'consumidor');
            if (data) setProfiles(data);
        };
        fetchProfiles();
    }, [isOpen]);

    useEffect(() => {
        if (externalResponsibles.length > 0) {
            setDashboardResponsibles(externalResponsibles);
            setResponsibleMode('custom');
        }
    }, [externalResponsibles]);

    useEffect(() => {
        if (isVendedor && user?.id) {
            setResponsibleMode('mine');
            setDashboardResponsibles([user.id]);
        }
    }, [isVendedor, user?.id]);

    useEffect(() => {
        if (!isOpen) return;
        fetchData();
    }, [isOpen, dateStart, dateEnd, dashboardResponsibles, responsibleMode]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Paginado: sem isso o PostgREST corta em 1.000 linhas e, com "todos", o painel
            // contava só uma parte das tarefas.
            const data = await buscarTudoPorChave<TarefaRow>(
                (apos, tamanho) => {
                    let query = supabase
                        .from('tarefas')
                        .select('id, created_at, vencimento, status, prioridade, responsavel_id, data_conclusao, categoria')
                        .order('id', { ascending: true })
                        .limit(tamanho);

                    if (responsibleMode === 'mine' && user?.id) {
                        query = query.eq('responsavel_id', user.id);
                    } else if (responsibleMode === 'custom' && dashboardResponsibles.length > 0) {
                        query = query.in('responsavel_id', dashboardResponsibles);
                    }
                    if (apos != null) query = query.gt('id', apos);
                    return query as any;
                },
                (r) => r.id,
            );
            setRows(data);
        } catch (err) {
            console.error('Dashboard tarefas fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const computed = useMemo(() => {
        const start = new Date(dateStart + 'T00:00:00');
        const end = new Date(dateEnd + 'T23:59:59');
        const isComp = responsibleMode === 'custom' && dashboardResponsibles.length >= 2;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Tarefa de quem saiu da empresa fica fora do painel (src/lib/tarefaArquivada.ts).
        // `profiles` aqui já é só de ativos, então quem não está nela conta como saiu.
        const tarefaArquivada = criarChecagemTarefaArquivada(profiles);
        const visiveis = rows.filter(r => !tarefaArquivada(r));

        const criadasInRange = visiveis.filter(r => {
            const d = new Date(r.created_at);
            if (onlyBusinessDays && (d.getDay() === 0 || d.getDay() === 6)) return false;
            return d >= start && d <= end;
        });

        const concluidasInRange = visiveis.filter(r => {
            if (r.status !== 'concluido') return false;
            const d = new Date(r.created_at);
            if (onlyBusinessDays && (d.getDay() === 0 || d.getDay() === 6)) return false;
            return d >= start && d <= end;
        });

        const atrasadasInRange = visiveis.filter(r => {
            if (r.status === 'concluido' || r.status === 'cancelado') return false;
            if (!r.vencimento) return false;
            const venc = new Date(r.vencimento + 'T00:00:00');
            if (onlyBusinessDays && (venc.getDay() === 0 || venc.getDay() === 6)) return false;
            return venc < today && venc >= start && venc <= end;
        });

        let avgDaysToComplete = 0;
        if (concluidasInRange.length > 0) {
            const totalDays = concluidasInRange.reduce((acc, r) => {
                const completionDateStr = r.data_conclusao || r.created_at;
                const diff = new Date(completionDateStr).getTime() - new Date(r.created_at).getTime();
                return acc + Math.max(0, diff / (1000 * 60 * 60 * 24));
            }, 0);
            avgDaysToComplete = Math.round(totalDays / concluidasInRange.length);
        }

        const completionRate = criadasInRange.length > 0
            ? Math.round((concluidasInRange.length / criadasInRange.length) * 100)
            : 0;

        const respStats: Record<string, { criadas: number, concluidas: number, atrasadas: number, statusCounts: Record<string, number>, totalDaysToComplete: number }> = {};
        if (isComp) {
            dashboardResponsibles.forEach(id => {
                respStats[id] = { criadas: 0, concluidas: 0, atrasadas: 0, statusCounts: {}, totalDaysToComplete: 0 };
            });
        }

        const dayMap = new Map<string, any>();
        const cursor = new Date(start);
        while (cursor <= end) {
            if (!onlyBusinessDays || (cursor.getDay() !== 0 && cursor.getDay() !== 6)) {
                const key = cursor.toISOString().split('T')[0];
                const label = cursor.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                const fullLabel = cursor.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

                const entry: any = { date: key, label, fullLabel, criadas: 0, concluidas: 0, atrasadas: 0 };
                if (isComp) {
                    dashboardResponsibles.forEach(id => {
                        entry[`criadas_${id}`] = 0;
                        entry[`concluidas_${id}`] = 0;
                        entry[`atrasadas_${id}`] = 0;
                    });
                }
                dayMap.set(key, entry);
            }
            cursor.setDate(cursor.getDate() + 1);
        }

        criadasInRange.forEach(r => {
            const key = new Date(r.created_at).toISOString().split('T')[0];
            const entry = dayMap.get(key);
            if (entry) {
                entry.criadas++;
                if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                    entry[`criadas_${r.responsavel_id}`]++;
                }
            }
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].criadas++;
            }
        });

        concluidasInRange.forEach(r => {
            const key = new Date(r.created_at).toISOString().split('T')[0];
            const entry = dayMap.get(key);
            if (entry) {
                entry.concluidas++;
                if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                    entry[`concluidas_${r.responsavel_id}`]++;
                }
            }
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].concluidas++;
                const completionDateStr = r.data_conclusao || r.created_at;
                const diff = new Date(completionDateStr).getTime() - new Date(r.created_at).getTime();
                respStats[r.responsavel_id].totalDaysToComplete += Math.max(0, diff / (1000 * 60 * 60 * 24));
            }
        });

        atrasadasInRange.forEach(r => {
            if (!r.vencimento) return;
            const key = r.vencimento;
            const entry = dayMap.get(key);
            if (entry) {
                entry.atrasadas++;
                if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                    entry[`atrasadas_${r.responsavel_id}`]++;
                }
            }
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].atrasadas++;
            }
        });

        const chartData = Array.from(dayMap.values());

        const totalCriadas = chartData.reduce((a, d) => a + d.criadas, 0);
        const totalConcluidas = chartData.reduce((a, d) => a + d.concluidas, 0);
        const totalAtrasadas = chartData.reduce((a, d) => a + d.atrasadas, 0);
        const days = chartData.length || 1;
        const avgCriadas = (totalCriadas / days).toFixed(1);
        const avgConcluidas = (totalConcluidas / days).toFixed(1);
        const avgAtrasadas = (totalAtrasadas / days).toFixed(1);

        const statusCounts: Record<string, number> = {};

        visiveis.forEach(r => {
            const d = new Date(r.created_at);
            if (d >= start && d <= end) {
                if (onlyBusinessDays && (d.getDay() === 0 || d.getDay() === 6)) return;
                statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;

                if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                    respStats[r.responsavel_id].statusCounts[r.status] = (respStats[r.responsavel_id].statusCounts[r.status] || 0) + 1;
                }
            }
        });

        return {
            statusCounts,
            respStats,
            criadas: criadasInRange.length,
            concluidas: concluidasInRange.length,
            atrasadas: atrasadasInRange.length,
            avgDaysToComplete,
            completionRate,
            chartData,
            totalCriadas, totalConcluidas, totalAtrasadas,
            avgCriadas, avgConcluidas, avgAtrasadas,
            days
        };
    }, [rows, dateStart, dateEnd, responsibleMode, dashboardResponsibles, profiles, onlyBusinessDays]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowResponsibleDropdown(false);
            }
        };
        if (showResponsibleDropdown) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showResponsibleDropdown]);

    if (!isOpen) return null;

    const updatedAt = new Date().toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });

    const getResponsibleLabel = () => {
        if (responsibleMode === 'mine') return 'Minhas tarefas';
        if (responsibleMode === 'all') return 'Todas as tarefas';
        if (dashboardResponsibles.length === 0) return 'Todas as tarefas';
        const names = dashboardResponsibles
            .map(id => profiles.find(p => p.id === id)?.full_name)
            .filter(Boolean);
        if (names.length <= 2) return names.join(', ');
        return `${names[0]}, +${names.length - 1}`;
    };

    const filteredProfiles = profiles.filter(p =>
        p.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const openResponsibleDropdown = () => {
        if (isVendedor) return;
        setPendingResponsibles([...dashboardResponsibles]);
        setPendingMode(responsibleMode);
        setShowResponsibleDropdown(!showResponsibleDropdown);
    };

    const handleSelectResponsible = (id: string) => {
        const next = pendingResponsibles.includes(id)
            ? pendingResponsibles.filter(x => x !== id)
            : [...pendingResponsibles, id];
        setPendingResponsibles(next);
        setPendingMode(next.length === 0 ? 'all' : 'custom');
    };

    const applyResponsibles = () => {
        setDashboardResponsibles([...pendingResponsibles]);
        setResponsibleMode(pendingMode);
        setShowResponsibleDropdown(false);
    };

    const statusConfig = {
        pendente: { label: 'Pendente', bg: 'bg-yellow-50 dark:bg-yellow-950/30', border: 'border-yellow-100 dark:border-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-300', labelColor: 'text-yellow-600 dark:text-yellow-400' },
        em_andamento: { label: 'Em andamento', bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-100 dark:border-blue-900/50', text: 'text-blue-800 dark:text-blue-300', labelColor: 'text-blue-600 dark:text-blue-400' },
        concluido: { label: 'Concluído', bg: 'bg-green-50 dark:bg-green-950/30', border: 'border-green-100 dark:border-green-900/50', text: 'text-green-800 dark:text-green-300', labelColor: 'text-green-600 dark:text-green-400' },
        cancelado: { label: 'Cancelado', bg: 'bg-gray-50 dark:bg-gray-800/50', border: 'border-gray-200 dark:border-gray-700', text: 'text-gray-800 dark:text-gray-200', labelColor: 'text-gray-500 dark:text-gray-400' },
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-2 sm:p-4 md:p-10">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
                onClick={onClose}
            />
            {/* Modal Container */}
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col my-auto mt-0 md:mt-0 animate-in zoom-in-95 slide-in-from-top-4 duration-300">

                {/* Header */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between p-4 md:p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 rounded-t-2xl relative z-10 gap-4">
                    <div className="flex items-center justify-between w-full lg:w-auto">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-primary/10 text-primary rounded-xl flex-shrink-0">
                                <BarChart3 size={22} />
                            </div>
                            <div>
                                <h2 className="text-base sm:text-lg font-bold text-gray-800 dark:text-white font-poppins">Dashboard de Tarefas</h2>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Visão geral das tarefas no período</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors lg:hidden shrink-0"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full lg:w-auto">
                        {/* Date Filter (Adapted from KanbanGraphic) */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm w-full lg:w-auto">
                            <div className="flex items-center gap-2 justify-between sm:justify-start w-full sm:w-auto">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase hidden sm:inline-block">Período:</span>
                                <div className="flex items-center justify-between sm:justify-start gap-2 flex-1 sm:flex-none">
                                    <input
                                        type="date"
                                        value={dateStart}
                                        onChange={e => setDateStart(e.target.value)}
                                        className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary w-full sm:w-auto"
                                    />
                                    <span className="text-gray-400">-</span>
                                    <input
                                        type="date"
                                        value={dateEnd}
                                        onChange={e => setDateEnd(e.target.value)}
                                        className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary w-full sm:w-auto"
                                    />
                                </div>
                            </div>
                            <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1"></div>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                                    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                                    setDateStart(firstDay.toISOString().split('T')[0]);
                                    setDateEnd(lastDay.toISOString().split('T')[0]);
                                }}
                                className="flex items-center justify-center gap-2 px-3 py-1.5 sm:py-1 bg-primary/10 dark:bg-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors w-full sm:w-auto"
                            >
                                <Calendar size={14} />
                                Mês Atual
                            </button>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors hidden lg:block shrink-0"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 p-4 md:p-6 space-y-6 relative z-20">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64">
                            <LoadingSpinner label="Carregando dados..." size="sm" />
                        </div>
                    ) : (
                        <>
                            {/* KPI Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 rounded-2xl p-5 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">Tarefas Atribuídas</p>
                                    <p className="text-4xl font-extrabold text-blue-800 dark:text-blue-300 font-mono">{computed.criadas}</p>
                                    {isComparative && (
                                        <div className="mt-auto pt-3 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[11px]">
                                                    <span className="flex items-center gap-1.5 text-blue-700/80 dark:text-blue-300/80 truncate">
                                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido'}</span>
                                                    </span>
                                                    <span className="font-bold text-blue-800 dark:text-blue-300">{computed.respStats[id]?.criadas || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900/50 rounded-2xl p-5 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-xs font-bold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">Concluídas</p>
                                    <p className="text-4xl font-extrabold text-green-800 dark:text-green-300 font-mono">{computed.concluidas}</p>
                                    {isComparative && (
                                        <div className="mt-auto pt-3 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[11px]">
                                                    <span className="flex items-center gap-1.5 text-green-700/80 dark:text-green-300/80 truncate">
                                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido'}</span>
                                                    </span>
                                                    <span className="font-bold text-green-800 dark:text-green-300">{computed.respStats[id]?.concluidas || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 rounded-2xl p-5 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">Em atraso</p>
                                    <p className="text-4xl font-extrabold text-red-800 dark:text-red-300 font-mono">{computed.atrasadas}</p>
                                    {isComparative && (
                                        <div className="mt-auto pt-3 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[11px]">
                                                    <span className="flex items-center gap-1.5 text-red-700/80 dark:text-red-300/80 truncate">
                                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido'}</span>
                                                    </span>
                                                    <span className="font-bold text-red-800 dark:text-red-300">{computed.respStats[id]?.atrasadas || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 transition-all hover:shadow-md flex flex-col relative z-20">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tempo médio conclusão</p>
                                        <div className="group relative">
                                            <Info size={14} className="text-gray-400 cursor-help" />
                                            <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-gray-900 text-white text-[11px] px-2 py-1 rounded-lg w-48 shadow-lg z-10">
                                                Média de dias entre a criação e a conclusão
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-4xl font-extrabold text-gray-800 dark:text-white font-mono">{computed.avgDaysToComplete}</span>
                                        <span className="text-sm text-gray-500 font-medium">dias</span>
                                    </div>
                                    {isComparative && (
                                        <div className="mt-auto pt-3 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => {
                                                const stats = computed.respStats[id];
                                                const avg = stats?.concluidas ? Math.round(stats.totalDaysToComplete / stats.concluidas) : 0;
                                                return (
                                                    <div key={id} className="flex items-center justify-between text-[11px]">
                                                        <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 truncate">
                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                            <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido'}</span>
                                                        </span>
                                                        <span className="font-bold text-gray-800 dark:text-gray-200">{avg} dias</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 transition-all hover:shadow-md flex flex-col relative z-20">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Taxa de conclusão</p>
                                        <div className="group relative">
                                            <Info size={14} className="text-gray-400 cursor-help" />
                                            <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-gray-900 text-white text-[11px] px-2 py-1 rounded-lg w-48 shadow-lg z-10">
                                                Percentual de tarefas concluídas em relação ao total criado no período
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-4xl font-extrabold text-gray-800 dark:text-white font-mono">{computed.completionRate}</span>
                                        <span className="text-sm text-gray-500 font-medium">%</span>
                                    </div>
                                    {isComparative && (
                                        <div className="mt-auto pt-3 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => {
                                                const stats = computed.respStats[id];
                                                const rate = stats?.criadas ? Math.round((stats.concluidas / stats.criadas) * 100) : 0;
                                                return (
                                                    <div key={id} className="flex items-center justify-between text-[11px]">
                                                        <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 truncate">
                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                            <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido'}</span>
                                                        </span>
                                                        <span className="font-bold text-gray-800 dark:text-gray-200">{rate}%</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Chart / Table Section */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 md:p-6">
                                {/* Section Header */}
                                <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-6 gap-4 relative z-50">
                                    <div>
                                        <h3 className="text-base font-bold text-gray-800 dark:text-white">
                                            {isComparative ? 'Comparativo de Equipe' : 'Tarefas Atribuídas, concluídas e atrasadas'}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                                            <Info size={12} />
                                            Atualizado em {updatedAt}
                                        </p>
                                    </div>

                                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full xl:w-auto">
                                        <div className="flex overflow-x-auto hide-scrollbar custom-scrollbar pb-1 sm:pb-0 gap-3 w-full sm:w-auto flex-1 sm:flex-none">
                                            <button
                                                onClick={() => setOnlyBusinessDays(!onlyBusinessDays)}
                                                className={`flex-shrink-0 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition-all border ${onlyBusinessDays ? 'bg-primary/10 border-primary/30 text-primary shadow-sm' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                                title="Ignorar sábados e domingos no gráfico e médias"
                                            >
                                                {onlyBusinessDays ? 'Apenas Dias Úteis' : 'Todos os Dias'}
                                            </button>

                                            {isComparative && chartType === 'line' && (
                                                <div className="flex-shrink-0 flex items-center gap-1 sm:gap-2 bg-gray-50 dark:bg-gray-800/50 p-1 sm:p-1.5 rounded-xl border border-gray-200 dark:border-gray-700">
                                                    <button
                                                        onClick={() => setCompareMetric('criadas')}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${compareMetric === 'criadas' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 shadow-sm' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                                    >
                                                        Atribuídas
                                                    </button>
                                                    <button
                                                        onClick={() => setCompareMetric('concluidas')}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${compareMetric === 'concluidas' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 shadow-sm' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                                    >
                                                        Concluídas
                                                    </button>
                                                    <button
                                                        onClick={() => setCompareMetric('atrasadas')}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${compareMetric === 'atrasadas' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 shadow-sm' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                                    >
                                                        Atrasadas
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
                                            {/* Responsible Selector Dropdown */}
                                            <div className="relative z-50 flex-1 sm:flex-none">
                                                <ResponsibleFilter
                                                    selectedResponsibles={
                                                        responsibleMode === 'mine' ? ['me'] : dashboardResponsibles
                                                    }
                                                    setSelectedResponsibles={(keys) => {
                                                        if (keys.includes('me')) {
                                                            setResponsibleMode('mine');
                                                            setDashboardResponsibles(user?.id ? [user.id] : []);
                                                        } else {
                                                            setResponsibleMode(keys.length === 0 ? 'all' : 'custom');
                                                            setDashboardResponsibles(keys);
                                                        }
                                                    }}
                                                    profiles={profiles as any}
                                                    currentUserProfile={profile}
                                                    viewContext="tarefas"
                                                />
                                            </div>

                                            {/* View Toggle: Line / Table */}
                                            <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                <button
                                                    onClick={() => setChartType('line')}
                                                    className={`p-1.5 rounded-md transition-all ${chartType === 'line'
                                                        ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                                        : 'text-gray-400 hover:text-gray-600'
                                                        }`}
                                                    title="Gráfico de linha"
                                                >
                                                    <LineChartIcon size={16} />
                                                </button>
                                                <button
                                                    onClick={() => setChartType('table')}
                                                    className={`p-1.5 rounded-md transition-all ${chartType === 'table'
                                                        ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                                        : 'text-gray-400 hover:text-gray-600'
                                                        }`}
                                                    title="Visualizar como tabela"
                                                >
                                                    <Table2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Content: Chart or Table */}
                                {chartType === 'line' ? (
                                    <div style={{ width: '100%', height: 320 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={computed.chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey="label"
                                                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                                                    axisLine={{ stroke: '#e5e7eb' }}
                                                    tickLine={false}
                                                    interval="preserveStartEnd"
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    allowDecimals={false}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        background: '#fff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                        fontSize: 13,
                                                    }}
                                                    labelStyle={{ fontWeight: 700, color: '#374151' }}
                                                />
                                                {!isComparative ? (
                                                    <>
                                                        <Line type="monotone" dataKey="criadas" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Atribuídas" />
                                                        <Line type="monotone" dataKey="concluidas" stroke="#22c55e" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Concluídas" />
                                                        <Line type="monotone" dataKey="atrasadas" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Em atraso" />
                                                    </>
                                                ) : (
                                                    dashboardResponsibles.map((id, idx) => {
                                                        const name = profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido';
                                                        return (
                                                            <Line
                                                                key={id}
                                                                type="monotone"
                                                                dataKey={`${compareMetric}_${id}`}
                                                                stroke={colors[idx % colors.length]}
                                                                strokeWidth={2.5}
                                                                dot={{ r: 3 }}
                                                                activeDot={{ r: 5 }}
                                                                name={name}
                                                            />
                                                        );
                                                    })
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    /* Table View */
                                    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-xl">
                                        <div className="max-h-[400px] overflow-y-auto custom-scrollbar min-w-[600px]">
                                            {!isComparative ? (
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-800/50 relative">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarefas Atribuídas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Concluídas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Em Atraso</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                                                        {computed.chartData.map((day, idx) => (
                                                            <tr key={day.date} className={`transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-200">{day.fullLabel}</td>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{day.criadas}</td>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{day.concluidas}</td>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{day.atrasadas}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="sticky bottom-0 z-10">
                                                        {/* Total Row */}
                                                        <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-800 dark:text-white uppercase">Total</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-blue-700 dark:text-blue-400 font-mono">{computed.totalCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-green-700 dark:text-green-400 font-mono">{computed.totalConcluidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-red-700 dark:text-red-400 font-mono">{computed.totalAtrasadas}</td>
                                                        </tr>
                                                        {/* Average Row */}
                                                        <tr className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-600 dark:text-gray-300 uppercase">Média / dia</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgConcluidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgAtrasadas}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            ) : (
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Responsável</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Atribuídas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Concluídas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Atrasadas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Taxa de Conclusão</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Média de Atribuídas por Dia">Média Atribuídas/Dia</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Média de Concluídas por Dia">Média Concluídas/Dia</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Média de Atrasadas por Dia">Média Atrasadas/Dia</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                                                        {dashboardResponsibles
                                                            .map(id => ({ id, name: profiles.find(p => p.id === id)?.full_name || 'Desconhecido', stats: computed.respStats[id] }))
                                                            .sort((a, b) => (b.stats?.concluidas || 0) - (a.stats?.concluidas || 0))
                                                            .map(({ id, name, stats }, idx) => {
                                                                const sInfo = stats || { criadas: 0, concluidas: 0, atrasadas: 0 };
                                                                const rateVal = sInfo.criadas > 0 ? (sInfo.concluidas / sInfo.criadas) * 100 : 0;
                                                                const tx = sInfo.criadas > 0 ? Math.round(rateVal) + '%' : '-';
                                                                const originalIndex = dashboardResponsibles.indexOf(id);

                                                                const avgCriadas = (sInfo.criadas / Math.max(1, computed.days)).toFixed(1);
                                                                const avgConcluidas = (sInfo.concluidas / Math.max(1, computed.days)).toFixed(1);
                                                                const avgAtrasadas = (sInfo.atrasadas / Math.max(1, computed.days)).toFixed(1);

                                                                return (
                                                                    <tr key={id} className={`transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[originalIndex % colors.length] }}></span>
                                                                            {name}
                                                                        </td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-blue-600 dark:text-blue-300 font-mono">{sInfo.criadas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-green-600 dark:text-green-300 font-mono font-bold">{sInfo.concluidas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-red-600 dark:text-red-300 font-mono">{sInfo.atrasadas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{tx}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-mono">{avgCriadas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-mono">{avgConcluidas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-mono">{avgAtrasadas}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                    <tfoot className="">
                                                        <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-800 dark:text-white uppercase">Total Equipe</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-blue-700 dark:text-blue-400 font-mono">{computed.totalCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-green-700 dark:text-green-400 font-mono">{computed.totalConcluidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-red-700 dark:text-red-400 font-mono">{computed.totalAtrasadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-700 dark:text-gray-300 font-mono">
                                                                {computed.totalCriadas > 0 ? Math.round((computed.totalConcluidas / computed.totalCriadas) * 100) + '%' : '-'}
                                                            </td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-600 dark:text-gray-400 font-mono">{computed.avgCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-600 dark:text-gray-400 font-mono">{computed.avgConcluidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-600 dark:text-gray-400 font-mono">{computed.avgAtrasadas}</td>
                                                        </tr>
                                                        {/* Average Row Comparativo */}
                                                        <tr className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-600 dark:text-gray-300 uppercase">Média / dia</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgConcluidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgAtrasadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Status Cards */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6">
                                <h3 className="text-base font-bold text-gray-800 dark:text-white mb-4">Tarefas por Status no período selecionado</h3>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    {Object.entries(statusConfig).map(([key, status]) => {
                                        const count = computed.statusCounts[key] || 0;
                                        return (
                                            <div key={key} className={`border rounded-2xl p-4 transition-all hover:shadow-md flex flex-col ${status.bg} ${status.border}`}>
                                                <p className={`text-[11px] font-bold uppercase tracking-wide mb-1 ${status.labelColor}`}>{status.label}</p>
                                                <p className={`text-2xl font-extrabold font-mono ${status.text}`}>
                                                    {count}
                                                </p>
                                                {isComparative && count > 0 && (
                                                    <div className="mt-auto pt-3 space-y-1">
                                                        {dashboardResponsibles
                                                            .filter(id => (computed.respStats[id]?.statusCounts[key] || 0) > 0)
                                                            .map(id => (
                                                                <div key={id} className={`flex items-center justify-between text-[10px] ${status.labelColor} opacity-80`}>
                                                                    <span className="flex items-center gap-1.5 truncate">
                                                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[dashboardResponsibles.indexOf(id) % colors.length] }}></span>
                                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido'}</span>
                                                                    </span>
                                                                    <span className="font-bold">{computed.respStats[id].statusCounts[key]}</span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 flex justify-end rounded-b-2xl relative z-10">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ModalTasksGraphic;
